import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { apiHeaders } from '@/config/axios';

import type {
  AssessmentConfig,
  AssessmentProgress,
  AssessmentStatus,
  CandidateAnswer,
} from '../types';

type AssessmentState = {
  token: string | null; // UUID — used as publicUrl for all API calls
  config: AssessmentConfig | null;
  status: AssessmentStatus;
  currentSectionIndex: number;
  currentQuestionIndex: number;
  answers: Record<string, CandidateAnswer>;
  startedAt: number | null;
  endsAt: number | null;
  hasHandicap: boolean; // accessibility extra-time request
  cheatingTries: number; // synced from backend progress
  // Module-level flag — survives component remounts (Suspense, HMR, strict mode).
  // Prevents useAutoSubmit from firing lockAssessment more than once per session.
  hasLocked: boolean;
};

type AssessmentActions = {
  setToken: (token: string) => void;
  setConfig: (config: AssessmentConfig) => void;
  setStatus: (status: AssessmentStatus) => void;
  setStarted: (startedAt: number, endsAt: number) => void;
  recordAnswer: (questionId: string, answer: CandidateAnswer) => void;
  advanceQuestion: () => void;
  goToPreviousQuestion: () => void;
  setHasHandicap: (value: boolean) => void;
  setCheatingTries: (count: number) => void;
  incrementLocalCheatingTries: () => void;
  applyProgress: (progress: AssessmentProgress) => void;
  forceTimeout: () => void;
  markLocked: () => void;
  reset: () => void;
};

export const useAssessmentStore = create<AssessmentState & AssessmentActions>()(
  devtools(
    immer((set) => ({
      token: null,
      config: null,
      status: 'idle',
      currentSectionIndex: 0,
      currentQuestionIndex: 0,
      answers: {},
      startedAt: null,
      endsAt: null,
      hasHandicap: false,
      cheatingTries: 0,
      hasLocked: false,

      setToken: (token) => {
        set((state) => {
          state.token = token;
        });
        apiHeaders.setAssessmentToken(token);
      },

      setConfig: (config) => {
        set((state) => {
          state.config = config;
        });
      },

      setStatus: (status) => {
        set((state) => {
          state.status = status;
        });
      },

      setStarted: (startedAt, endsAt) => {
        set((state) => {
          state.startedAt = startedAt;
          state.endsAt = endsAt;
          state.status = 'active';
        });
      },

      recordAnswer: (questionId, answer) => {
        set((state) => {
          state.answers[questionId] = answer;
        });
      },

      advanceQuestion: () => {
        set((state) => {
          const { config, currentSectionIndex, currentQuestionIndex } = state;
          if (!config) return;

          const currentSection = config.sections[currentSectionIndex];
          if (!currentSection) return;

          const isLastQuestion = currentQuestionIndex >= currentSection.questions.length - 1;
          const isLastSection = currentSectionIndex >= config.sections.length - 1;

          if (!isLastQuestion) {
            state.currentQuestionIndex = currentQuestionIndex + 1;
          } else if (!isLastSection) {
            state.currentSectionIndex = currentSectionIndex + 1;
            state.currentQuestionIndex = 0;
          }
          // Note: when the last question is reached, navigation is driven by
          // the answer API response (feedbackRequired / finished flags).
          // Do NOT set status = 'submitting' here anymore.
        });
      },

      goToPreviousQuestion: () => {
        set((state) => {
          if (state.currentQuestionIndex > 0) {
            state.currentQuestionIndex = state.currentQuestionIndex - 1;
          } else if (state.currentSectionIndex > 0) {
            state.currentSectionIndex = state.currentSectionIndex - 1;
            const prevSection = state.config?.sections[state.currentSectionIndex];
            if (prevSection) {
              state.currentQuestionIndex = prevSection.questions.length - 1;
            }
          }
        });
      },

      setHasHandicap: (value) => {
        set((state) => {
          state.hasHandicap = value;
        });
      },

      setCheatingTries: (count) => {
        set((state) => {
          state.cheatingTries = count;
        });
      },

      incrementLocalCheatingTries: () => {
        set((state) => {
          state.cheatingTries = state.cheatingTries + 1;
        });
      },

      applyProgress: (progress) => {
        set((state) => {
          state.currentSectionIndex = progress.currentSectionIndex;
          state.currentQuestionIndex = progress.currentQuestionIndex;
          state.answers = progress.answers;
          state.startedAt = progress.startedAt;
          state.endsAt = progress.endsAt;
          state.cheatingTries = progress.cheatingTries;
        });
      },

      forceTimeout: () => {
        set((state) => {
          state.status = 'timed-out';
        });
      },

      markLocked: () => {
        set((state) => {
          state.hasLocked = true;
        });
      },

      reset: () => {
        set((state) => {
          state.token = null;
          state.config = null;
          state.status = 'idle';
          state.currentSectionIndex = 0;
          state.currentQuestionIndex = 0;
          state.answers = {};
          state.startedAt = null;
          state.endsAt = null;
          state.hasHandicap = false;
          state.cheatingTries = 0;
          state.hasLocked = false;
        });
        apiHeaders.clearAssessmentToken();
      },
    })),
    { name: 'AssessmentStore' },
  ),
);

// ─── Derived selectors ────────────────────────────────────────────────────────

export function selectCurrentSection(state: AssessmentState) {
  return state.config?.sections[state.currentSectionIndex] ?? null;
}

export function selectCurrentQuestion(state: AssessmentState) {
  const section = state.config?.sections[state.currentSectionIndex];
  return section?.questions[state.currentQuestionIndex] ?? null;
}

export function selectIsFirstQuestion(state: AssessmentState): boolean {
  return state.currentSectionIndex === 0 && state.currentQuestionIndex === 0;
}

export function selectTotalAnswered(state: AssessmentState): number {
  return Object.values(state.answers).filter((a) => a.value !== null || a.iDontKnow).length;
}
